import { ClassFormatEnum, ClassMatchType, MatchBracketType } from '../../types/enums';
import type { Class } from '../../types/tournament';
import type { IMatchScheduleItem } from '../../types/schedule';
import {
  isKnockoutStageMatch,
  MatchScheduleInterval,
  schedulingRoundKey,
} from './scheduleConflictDetector';

const FINALS_GOLD_ROUND = 14;

export function isDoubleElimClass(
  classObject: Class | undefined,
  classIntervals: MatchScheduleInterval[]
): boolean {
  const format = classObject?.format;
  if (format !== ClassFormatEnum.BRACKET && format !== ClassFormatEnum.GROUP_BRACKET) {
    return false;
  }
  return classIntervals.some(
    (i) =>
      isKnockoutStageMatch(i.match) &&
      (i.match.bracket === MatchBracketType.WINNERS ||
        i.match.bracket === MatchBracketType.LOSERS)
  );
}

export function koIntervalsForPhaseIndex(
  classIntervals: MatchScheduleInterval[],
  unscheduledKoMatches: IMatchScheduleItem[] = []
): MatchScheduleInterval[] {
  const synthetic: MatchScheduleInterval[] = unscheduledKoMatches
    .filter((m) => isKnockoutStageMatch(m))
    .map((match) => ({
      match,
      startMs: 0,
      endMs: 0,
      dayKey: 0,
      courtId: null,
      missingEnd: false,
    }));
  return [...classIntervals, ...synthetic];
}

export function buildDePhaseIndex(
  classIntervals: MatchScheduleInterval[]
): Map<number, number> {
  const phaseIndexByMatchId = new Map<number, number>();
  const ko = classIntervals.filter((i) => isKnockoutStageMatch(i.match));
  if (ko.length === 0) return phaseIndexByMatchId;

  const wb = ko.filter((i) => i.match.bracket === MatchBracketType.WINNERS);
  const lb = ko.filter((i) => i.match.bracket === MatchBracketType.LOSERS);
  const gf = ko.filter((i) => i.match.type === ClassMatchType.FINAL && i.match.isGold);

  const wbRounds = [...new Set(wb.map((i) => i.match.type))].sort((a, b) => a - b);
  const lbRounds = [...new Set(lb.map((i) => i.match.type))].sort((a, b) => a - b);

  let phase = 0;
  for (const r of wbRounds) {
    if (r >= FINALS_GOLD_ROUND) continue;
    for (const interval of wb) {
      if (interval.match.type === r) {
        phaseIndexByMatchId.set(interval.match.id, phase);
      }
    }
    phase += 1;
  }

  let lbOffset = 1;
  if (wbRounds.length >= 2) {
    const wbR1Count = wb.filter((i) => i.match.type === wbRounds[0]).length;
    const wbR2Count = wb.filter((i) => i.match.type === wbRounds[1]).length;
    if (wbR1Count <= wbR2Count) {
      lbOffset = 2;
    }
  }

  for (let j = 0; j < lbRounds.length; j++) {
    const depth = j + lbOffset;
    const r = lbRounds[j];
    for (const interval of lb) {
      if (interval.match.type === r) {
        phaseIndexByMatchId.set(interval.match.id, depth);
      }
    }
  }

  const maxPhase = Math.max(-1, ...phaseIndexByMatchId.values());
  for (const interval of gf) {
    phaseIndexByMatchId.set(interval.match.id, maxPhase + 1);
  }

  return phaseIndexByMatchId;
}

export function buildBracketTypePhaseLookup(
  classIntervals: MatchScheduleInterval[]
): Map<string, number> {
  const phaseByMatchId = buildDePhaseIndex(classIntervals);
  const lookup = new Map<string, number>();
  for (const interval of classIntervals) {
    if (!isKnockoutStageMatch(interval.match)) continue;
    const phase = phaseByMatchId.get(interval.match.id);
    if (phase == null) continue;
    const key = `${interval.match.bracket}-${interval.match.type}`;
    if (!lookup.has(key)) {
      lookup.set(key, phase);
    }
  }
  return lookup;
}

export function inferPhaseFromBracketAndType(
  match: IMatchScheduleItem,
  classIntervals: MatchScheduleInterval[]
): number | null {
  const lookup = buildBracketTypePhaseLookup(classIntervals);
  return lookup.get(`${match.bracket}-${match.type}`) ?? null;
}

export function schedulingOrderKey(
  match: IMatchScheduleItem,
  classObject: Class | undefined,
  dePhaseIndex: Map<number, number> | null,
  classIntervals?: MatchScheduleInterval[] | null
): number | null {
  if (!isKnockoutStageMatch(match)) return null;
  if (dePhaseIndex != null) {
    let phase = dePhaseIndex.get(match.id);
    if (phase == null && classIntervals?.length) {
      phase = inferPhaseFromBracketAndType(match, classIntervals) ?? undefined;
    }
    if (phase != null) return phase;
  }
  return schedulingRoundKey(match, classObject);
}
